import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Routes, Route } from 'react-router-dom';
import { io } from "socket.io-client";
import './App.css';

// Components
import Dashboard from './components/Dashboard';
import Tracking from './components/Tracking';
import Rating from './components/Rating';
import Settings from './components/Settings';
import ToolPlaceholder from './components/ToolPlaceholder';
import SearchZone from './components/SearchZone';
import AddressValidation from './components/AddressValidation';
import UserAccess from './components/UserAccess';
import Shipping from './components/Shipping';
import BulkShipping from './components/BulkShipping';
import Billing from './components/Billing';
import ShipmentDetails from './shipment-details';
import LoadingBar from './components/LoadingBar';
import Company from './components/Company';
import FCLabel from './components/FCLabel';
import RequestFC from './components/RequestFC';
import Reports from './components/Reports';
import WebhookDocs from './components/WebhookDocs';
import OpsSecurityScanning from './components/OpsSecurityScanning';
import OpsReceiving from './components/OpsReceiving';

const UPS_DOMAIN = '@ups.com';

const App = () => {
  const [authToken, setAuthToken] = useState(localStorage.getItem('authToken') || null);
  const [authEmail, setAuthEmail] = useState(localStorage.getItem('authEmail') || "");
  const [authPassword, setAuthPassword] = useState("");
  const [authMode, setAuthMode] = useState("login"); // 'login' | 'signup' | 'forgot'
  const [authMessage, setAuthMessage] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Processing...");
  const [setupToken, setSetupToken] = useState(null);
  const [tempPassword, setTempPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [activeTool, setActiveTool] = useState(null);
  const [activeUsers, setActiveUsers] = useState(0);
  const [onlineUsersList, setOnlineUsersList] = useState([]);
  const [showOnlineUsers, setShowOnlineUsers] = useState(false);
  const [userModules, setUserModules] = useState([]);
  const [userAccounts, setUserAccounts] = useState([]);
  const [importAccounts, setImportAccounts] = useState([]);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [userCity, setUserCity] = useState(localStorage.getItem('userCity') || "");
  const [userCompanySlug, setUserCompanySlug] = useState(localStorage.getItem('userCompanySlug') || "");
  const [userCompanyName, setUserCompanyName] = useState(localStorage.getItem('userCompanyName') || "");
  const [userCompanyModules, setUserCompanyModules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(null);

  // Feedback state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackSubject, setFeedbackSubject] = useState("WEB SHIPPING Feedback");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackSending, setFeedbackSending] = useState(false);

  // Auth Server URL
  const isDev = process.env.NODE_ENV === 'development';
  const backendUrl = isDev ? `${window.location.protocol}//${window.location.hostname}:5001` : '';
  const AUTH_SERVER = `${backendUrl}/api/auth`;
  const API_BASE_URL = `${backendUrl}/api`;
  const loadingCount = useRef(0);
  const socketRef = useRef(null);

  const updateLoadingCount = (delta) => {
    loadingCount.current += delta;
    if (loadingCount.current > 0) {
      setIsLoading(true);
    } else {
      loadingCount.current = 0;
      setIsLoading(false);
    }
  };

  // Axios Interceptors for global loading bar
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use((config) => {
      updateLoadingCount(1);
      return config;
    }, (error) => {
      updateLoadingCount(-1);
      return Promise.reject(error);
    });

    const responseInterceptor = axios.interceptors.response.use((response) => {
      updateLoadingCount(-1);
      return response;
    }, (error) => {
      updateLoadingCount(-1);
      return Promise.reject(error);
    });

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Dynamic loading message for auth
  useEffect(() => {
    let interval;
    if (authLoading) {
      const messages = [
        "Processing...",
        "Creating Access...",
        "Creating Protection Firewall...",
        "Assigning Access..."
      ];
      let msgIndex = 0;
      setLoadingMessage(messages[0]);
      interval = setInterval(() => {
        msgIndex = (msgIndex + 1) % messages.length;
        setLoadingMessage(messages[msgIndex]);
      }, 1500);
    } else {
      setLoadingMessage("Processing...");
    }
    return () => clearInterval(interval);
  }, [authLoading]);

  // Trigger loading briefly on tool switch
  useEffect(() => {
    if (activeTool) {
      updateLoadingCount(1);
      const timer = setTimeout(() => updateLoadingCount(-1), 1500);
      return () => clearTimeout(timer);
    }
  }, [activeTool]);

  useEffect(() => {
    if (authToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      localStorage.setItem('authToken', authToken);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('authToken');
    }
  }, [authToken]);

  // When authToken is present, decode basic fields from the JWT so child components
  // receive the current user and modules immediately (helps Shipping filtering).
  useEffect(() => {
    if (!authToken) {
      setUserCompanySlug("");
      setUserCompanyName("");
      setUserCompanyModules([]);
      return;
    }
    try {
      const payload = authToken.split('.')[1];
      const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      if (decoded) {
        setUser(decoded);
        if (decoded.email) setAuthEmail(decoded.email);
        if (decoded.modules) setUserModules(decoded.modules || []);
        if (decoded.accounts) setUserAccounts(decoded.accounts || []);
        if (decoded.importAccounts) setImportAccounts(decoded.importAccounts || []);
        if (typeof decoded.isSuperUser !== 'undefined') setIsSuperUser(decoded.isSuperUser);
        // Extract company context from token
        if (decoded.companySlug) {
          setUserCompanySlug(decoded.companySlug);
          localStorage.setItem('userCompanySlug', decoded.companySlug);
          // Fetch company name and modules
          const apiUrl = process.env.NODE_ENV === 'development' ? `${window.location.protocol}//${window.location.hostname}:5001/api` : '/api';
          axios.get(`${apiUrl}/companies/${decoded.companySlug}`).then(res => {
            console.log('[Company Fetch] Response:', res.data);
            if (res.data?.success && res.data?.company) {
              const comp = res.data.company;
              console.log('[Company Fetch] Setting company data:', comp.name, comp.modules);
              setUserCompanyName(comp.name);
              setUserCompanyModules(comp.modules || []);
              localStorage.setItem('userCompanyName', comp.name);
            } else {
              console.warn('[Company Fetch] Invalid response format:', res.data);
            }
          }).catch(err => {
            console.error('[Company Fetch] Error:', err.response?.status, err.response?.data || err.message);
          });
        } else {
          setUserCompanySlug("");
          setUserCompanyName("");
          setUserCompanyModules([]);
        }
      }
    } catch (e) {
      // ignore decode errors
      console.warn('Failed to decode auth token', e);
    }
  }, [authToken]);

  // Verify user status periodically - logout if user was deleted
  useEffect(() => {
    if (!authToken) return;

    const verifyUserStatus = async () => {
      try {
        const res = await axios.get(
          process.env.NODE_ENV === 'development'
            ? `${window.location.protocol}//${window.location.hostname}:5001/api/auth/verify-user`
            : '/api/auth/verify-user'
        );

        if (res.data?.success) {
          console.log('[Verify] User is still active:', res.data.user?.email);
          // Update user accounts and modules from verify endpoint
          if (res.data.user?.accounts) {
            setUserAccounts(res.data.user.accounts);
          }
          if (res.data.user?.modules) {
            setUserModules(res.data.user.modules);
          }
          if (res.data.user?.importAccounts) {
            setImportAccounts(res.data.user.importAccounts);
          }
          if (res.data.user) {
            setUser(res.data.user);
          }

          // If user belongs to a company, also refresh company info on activity
          if (userCompanySlug) {
            const apiUrl = process.env.NODE_ENV === 'development' ? `${window.location.protocol}//${window.location.hostname}:5001/api` : '/api';
            axios.get(`${apiUrl}/companies/${userCompanySlug}`).then(cRes => {
              if (cRes.data?.success && cRes.data?.company) {
                const comp = cRes.data.company;
                setUserCompanyName(comp.name);
                setUserCompanyModules(comp.modules || []);
                localStorage.setItem('userCompanyName', comp.name);
              }
            }).catch(cErr => {
              console.error('[Verify Company] Error:', cErr.response?.status);
              // If company is deleted (404), logout user
              if (cErr.response?.status === 404) {
                console.warn('[Verify Company] Company was deleted, logging out');
                setAuthMessage('Your company account has been deactivated. You are being logged out.');
                setAuthToken(null);
                setAuthEmail("");
                localStorage.removeItem('authToken');
                localStorage.removeItem('authEmail');
                localStorage.removeItem('userCompanySlug');
                localStorage.removeItem('userCompanyName');
              }
            });
          }
        } else {
          console.warn('[Verify] User verification failed:', res.data?.reason);
          // Auto-logout if user was deleted or not found
          if (res.data?.reason === 'USER_DELETED' || res.data?.reason === 'USER_NOT_FOUND') {
            console.warn('[Verify] User was deleted/removed, logging out automatically');
            setAuthMessage('Your account has been deleted. You are being logged out.');
            setAuthToken(null);
            setAuthEmail("");
            localStorage.removeItem('authToken');
            localStorage.removeItem('authEmail');
            localStorage.removeItem('userCompanySlug');
            localStorage.removeItem('userCompanyName');
          }
        }
      } catch (err) {
        if (err.response?.status === 403) {
          console.warn('[Verify] User account deleted or removed');
          setAuthMessage('Your account has been deleted. You are being logged out.');
          setAuthToken(null);
          setAuthEmail("");
          localStorage.removeItem('authToken');
          localStorage.removeItem('authEmail');
          localStorage.removeItem('userCompanySlug');
          localStorage.removeItem('userCompanyName');
        } else if (err.response?.status === 401) {
          console.warn('[Verify] Token expired or invalid');
          setAuthToken(null);
        }
      }
    };

    // Verify immediately on login
    verifyUserStatus();

    // Verify on user activity (clicks, key presses) instead of periodic interval
    const handleActivity = () => {
      const now = Date.now();
      // Only verify if it's been more than 5 minutes since the last check to avoid excessive API calls
      const lastCheck = parseInt(localStorage.getItem('lastUserVerify') || '0');
      if (now - lastCheck > 300000) {
        localStorage.setItem('lastUserVerify', now.toString());
        verifyUserStatus();

        // Also update online heartbeat heartbeat (lastSeen) on activity
        if (authEmail && process.env.NODE_ENV !== 'development') {
          axios.post(`${API_BASE_URL}/users/online`, { email: authEmail }).catch(console.error);
        }
      }
    };

    window.addEventListener('click', handleActivity);
    window.addEventListener('keydown', handleActivity);

    return () => {
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('keydown', handleActivity);
    };
  }, [authToken]);

  useEffect(() => {
    // For Firebase deployment, we use REST API polling instead of Socket.IO
    // because Firebase Cloud Functions don't support WebSocket connections
    const isProduction = process.env.NODE_ENV !== 'development';

    if (isProduction) {
      // Firebase production: use REST API polling
      const fetchOnlineUsers = async () => {
        try {
          const res = await axios.get(`${API_BASE_URL}/users/online`);
          if (res.data?.success) {
            setActiveUsers(res.data.count || 0);
            setOnlineUsersList(res.data.users || []);
          }
        } catch (err) {
          console.error('[Online Users API Error]', err);
        }
      };

      // Initial fetch
      fetchOnlineUsers();

      // Auto-polling removed as per user request to remove all auto-refresh features

      // Register this user as online
      if (authEmail) {
        axios.post(`${API_BASE_URL}/users/online`, { email: authEmail }).catch(console.error);
      }

      return () => {
        // Remove this user from online list
        if (authEmail) {
          axios.delete(`${API_BASE_URL}/users/online`, { data: { email: authEmail } }).catch(console.error);
        }
      };
    } else {
      // Local development: use Socket.IO
      const socketURL = process.env.NODE_ENV === 'development' ? `${window.location.protocol}//${window.location.hostname}:5001` : '';

      const socket = io(socketURL, {
        transports: ['websocket'],
        withCredentials: true,
        auth: {
          email: authEmail,
          token: authToken
        }
      });
      socketRef.current = socket;

      socket.on('activeUsersCount', (count) => {
        console.log('[Socket] Active users count:', count);
        setActiveUsers(count);
      });

      socket.on('onlineUsers', (users) => {
        console.log('[Socket] Online users list:', users);
        setActiveUsers(users.length);
        setOnlineUsersList(users);
      });

      socket.on('connect', () => {
        console.log('[Socket] Connected, requesting online users');
        socket.emit('requestOnlineUsers');
      });

      socket.on('disconnect', () => {
        console.log('[Socket] Disconnected');
      });

      socket.on('connect_error', (error) => {
        console.error('[Socket] Connection error:', error);
      });

      return () => socket.disconnect();
    }
  }, [authEmail, authToken, API_BASE_URL]);

  // Refresh Online Users
  const handleRefreshOnlineUsers = () => {
    console.log('[Socket] Requesting online users refresh');
    if (socketRef.current) {
      socketRef.current.emit('requestOnlineUsers');
    }
  };

  // Auth Handlers
  const normalizeEmailInput = (raw) => {
    if (!raw) return '';
    const trimmed = raw.trim().toLowerCase();
    return trimmed.includes('@') ? trimmed : `${trimmed}${UPS_DOMAIN}`;
  };

  const handleLogin = async () => {
    setAuthMessage("");
    setAuthLoading(true);
    try {
      const email = normalizeEmailInput(authEmail);
      const res = await axios.post(`${AUTH_SERVER}/login`, { email, password: authPassword });
      if (res.data?.success && res.data?.token) {
        setAuthToken(res.data.token);
        setUserModules(res.data.user?.modules || []);
        setUserAccounts(res.data.user?.accounts || []);
        setImportAccounts(res.data.user?.importAccounts || []);
        setIsSuperUser(res.data.user?.isSuperUser || false);
        const city = res.data.user?.city || "";
        setUserCity(city);
        localStorage.setItem('userCity', city);
        localStorage.setItem('authEmail', email);
        setAuthMessage("Login successful.");
      } else {
        setAuthMessage(res.data?.message || "Login failed.");
      }
    } catch (err) {
      setAuthMessage(err.response?.data?.message || err.response?.data?.error || "Login failed.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignup = async () => {
    setAuthMessage("");
    setAuthLoading(true);
    try {
      const email = normalizeEmailInput(authEmail);
      const res = await axios.post(`${AUTH_SERVER}/signup`, { email });
      setAuthMessage(res.data?.message || "Temp password sent.");
      if (res.data?.success) setAuthMode("verify");
    } catch (err) {
      setAuthMessage(err.response?.data?.message || "Signup failed.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setAuthMessage("");
    setAuthLoading(true);
    try {
      const email = normalizeEmailInput(authEmail);
      const res = await axios.post(`${AUTH_SERVER}/forgot-password`, { email });
      setAuthMessage(res.data?.message || "Reset code sent.");
      if (res.data?.success) setAuthMode("verify");
    } catch (err) {
      setAuthMessage(err.response?.data?.message || "Request failed.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleVerify = async () => {
    setAuthMessage("");
    setAuthLoading(true);
    try {
      const email = normalizeEmailInput(authEmail);
      const res = await axios.post(`${AUTH_SERVER}/verify-temp`, { email, tempPassword });
      if (res.data?.success && res.data?.setupToken) {
        setSetupToken(res.data.setupToken);
        setAuthMode("set-password");
        setAuthMessage("Code verified. Please set your new password.");
      } else {
        setAuthMessage(res.data?.message || "Verification failed.");
      }
    } catch (err) {
      setAuthMessage(err.response?.data?.message || "Verification failed.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSetPassword = async () => {
    setAuthMessage("");
    setAuthLoading(true);
    try {
      const res = await axios.post(`${AUTH_SERVER}/set-password`, { setupToken, newPassword });
      if (res.data?.success) {
        setAuthMessage("Password set successfully! Please login.");
        setAuthMode("login");
        setAuthPassword("");
      } else {
        setAuthMessage(res.data?.message || "Failed to set password.");
      }
    } catch (err) {
      setAuthMessage(err.response?.data?.message || "Failed to set password.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    setAuthToken(null);
    setAuthEmail("");
    setAuthPassword("");
    setAuthMessage("Logged out.");
    setActiveTool(null);
    localStorage.removeItem('authEmail');
  };

  const handleFeedback = async () => {
    if (!feedbackMessage.trim()) return;
    setFeedbackSending(true);
    // Simple alert for now, could integrate with server.js later
    setTimeout(() => {
      alert("Feedback sent successfully!");
      setShowFeedbackModal(false);
      setFeedbackSending(false);
      setFeedbackMessage("");
    }, 1000);
  };

  // Helpers shared with components
  const findFirstScan = (activity) => {
    if (!Array.isArray(activity) || activity.length === 0) return { text: "No scan data" };
    // Simplified version for the refactor
    const sorted = [...activity].sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
    const first = sorted[0];
    return { text: `Pickup Scan at ${first.time} ${first.date}` };
  };

  const isDelivered = (tracking) => {
    if (!tracking || !tracking.data) return false;
    const current = tracking.data.currentStatus;
    const code = current?.statusCode || current?.status?.statusCode;
    return code === "011" || String(current?.description).toLowerCase().includes('delivered');
  };

  // Render Auth UI
  if (!authToken) {
    return (
      <div className="auth-wrapper">
        <div className="auth-card">
          <div className="auth-logo-section">
            <div className="logo-icon">WB</div>
            <h2>WEB SHIPPING</h2>
          </div>
          <p className="auth-sub">Logistics Management System</p>

          <div className="auth-tabs">
            {(authMode === "login" || authMode === "signup" || authMode === "forgot") && (
              <>
                <button className={authMode === "login" ? "active" : ""} onClick={() => { setAuthMode("login"); setAuthMessage(""); }}>Login</button>
                <button className={authMode === "signup" ? "active" : ""} onClick={() => { setAuthMode("signup"); setAuthMessage(""); }}>Signup</button>
                <button className={authMode === "forgot" ? "active" : ""} onClick={() => { setAuthMode("forgot"); setAuthMessage(""); }}>Forgot</button>
              </>
            )}
            {authMode === "verify" && <button className="active">Verify Code</button>}
            {authMode === "set-password" && <button className="active">Set Password</button>}
          </div>

          <form className="auth-form" onSubmit={(e) => {
            e.preventDefault();
            if (authMode === "login") handleLogin();
            else if (authMode === "signup") handleSignup();
            else if (authMode === "forgot") handleForgotPassword();
            else if (authMode === "verify") handleVerify();
            else if (authMode === "set-password") handleSetPassword();
          }}>
            {(authMode === "login" || authMode === "signup" || authMode === "forgot" || authMode === "verify") && (
              <div className="form-group">
                <label>Email / Username</label>
                <input
                  type="text"
                  placeholder="UPS Username"
                  value={authEmail}
                  onChange={(e) => setAuthEmail(e.target.value)}
                  disabled={authMode === "verify"}
                />
              </div>
            )}

            {authMode === "login" && (
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={authPassword}
                  onChange={(e) => setAuthPassword(e.target.value)}
                />
              </div>
            )}

            {authMode === "verify" && (
              <div className="form-group">
                <label>Temporary Password (from Email)</label>
                <input
                  type="text"
                  placeholder="6-digit code"
                  value={tempPassword}
                  onChange={(e) => setTempPassword(e.target.value)}
                />
              </div>
            )}

            {authMode === "set-password" && (
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
            )}

            <button className="auth-btn" disabled={authLoading}>
              {authLoading ? loadingMessage : (
                authMode === "login" ? "Sign In" :
                  authMode === "signup" ? "Request Access" :
                    authMode === "forgot" ? "Send Reset Code" :
                      authMode === "verify" ? "Verify Code" : "Set Password"
              )}
            </button>

            {authMode === "login" && (
              <div style={{ textAlign: 'center', marginTop: '10px' }}>
                <button type="button" className="auth-link-btn" onClick={() => setAuthMode("forgot")}>
                  Forgot Password?
                </button>
              </div>
            )}

            {(authMode === "verify" || authMode === "set-password" || authMode === "forgot") && (
              <button type="button" className="auth-link-btn" onClick={() => setAuthMode("login")}>
                Back to Login
              </button>
            )}
          </form>
          {authMessage && <p className={`auth-message ${authMessage.toLowerCase().includes('failed') || authMessage.toLowerCase().includes('error') ? 'error' : ''}`}>{authMessage}</p>}
        </div>
      </div>
    );
  }

  // Render Tool
  const renderTool = () => {
    switch (activeTool) {
      case 'tracking':
        return (
          <Tracking
            onBack={() => setActiveTool(null)}
            authEmail={authEmail}
            apiBaseUrl={API_BASE_URL}
            findFirstScan={findFirstScan}
            isDelivered={isDelivered}
          />
        );
      case 'rating':
        return (
          <Rating
            onBack={() => setActiveTool(null)}
            userAccounts={userAccounts}
            importAccounts={importAccounts}
            isSuperUser={isSuperUser}
            companySlug={userCompanySlug}
          />
        );
      case 'settings':
        return (
          <Settings
            onBack={() => setActiveTool(null)}
            currentUser={authEmail}
            isSuperUser={isSuperUser}
            companySlug={userCompanySlug}
            companyName={userCompanyName}
          />
        );
      case 'search-zone':
        return (
          <SearchZone
            onBack={() => setActiveTool(null)}
          />
        );
      case 'address-validation':
        return (
          <AddressValidation
            onBack={() => setActiveTool(null)}
          />
        );
      case 'user-access':
        return (
          <UserAccess
            onBack={() => setActiveTool(null)}
            companySlug={userCompanySlug}
            companyName={userCompanyName}
            companyModules={userCompanyModules}
            isSuperUser={isSuperUser}
            currentUser={authEmail}
          />
        );
      case 'shipping':
        return (
          <Shipping
            onBack={() => setActiveTool(null)}
            userModules={userModules}
            userAccounts={userAccounts}
            isSuperUser={isSuperUser}
            currentUser={authEmail}
            userCity={userCity}
            companySlug={userCompanySlug}
          />
        );
      case 'bulk-shipping':
        return (
          <BulkShipping
            onBack={() => setActiveTool(null)}
            userModules={userModules}
            userAccounts={userAccounts}
            isSuperUser={isSuperUser}
            currentUser={authEmail}
            userCity={userCity}
            companySlug={userCompanySlug}
            companyName={userCompanyName}
          />
        );
      case 'billing':
        return (
          <Billing
            onBack={() => setActiveTool(null)}
            isSuperUser={isSuperUser}
            currentUser={authEmail}
            companySlug={userCompanySlug}
          />
        );
      case 'fc-label':
        return (
          <FCLabel
            onBack={() => setActiveTool(null)}
            userAccounts={userAccounts}
            importAccounts={importAccounts}
            isSuperUser={isSuperUser}
            currentUser={authEmail}
            companySlug={userCompanySlug}
          />
        );
      case 'request-fc':
        return (
          <RequestFC
            onBack={() => setActiveTool(null)}
            user={user}
            fcRole={user?.fcRole}
            isSuperUser={isSuperUser}
            userAccounts={userAccounts}
            importAccounts={importAccounts}
            currentUser={authEmail}
            currentUserEmail={authEmail}
            companySlug={userCompanySlug}
          />
        );
      case 'company':
        return (
          <Company
            onBack={() => setActiveTool(null)}
            currentUser={authEmail}
            isSuperUser={isSuperUser}
          />
        );
      case 'reports':
        return (
          <Reports
            onBack={() => setActiveTool(null)}
            isSuperUser={isSuperUser}
            companySlug={userCompanySlug}
          />
        );
      case 'webhook-docs':
        return (
          <WebhookDocs
            onBack={() => setActiveTool(null)}
            companySlug={userCompanySlug}
            companyName={userCompanyName}
            companyModules={userCompanyModules}
          />
        );
      case 'ops-security-scanning':
        return (
          <OpsSecurityScanning
            onBack={() => setActiveTool(null)}
            currentUser={authEmail}
            isSuperUser={isSuperUser}
            companySlug={userCompanySlug}
          />
        );
      case 'ops-receiving':
        return (
          <OpsReceiving
            onBack={() => setActiveTool(null)}
            currentUser={authEmail}
            isSuperUser={isSuperUser}
            companySlug={userCompanySlug}
          />
        );
      default:
        return <Dashboard onSelectTool={(id) => setActiveTool(id)} userModules={userModules} isSuperUser={isSuperUser} companyName={userCompanyName} companySlug={userCompanySlug} />;
    }
  };

  return (
    <div className="App">
      <LoadingBar isLoading={isLoading} />
      <nav className="top-nav">
        <div className="nav-left">
          <div className="nav-logo" onClick={() => setActiveTool(null)}>WB</div>
          <span className="nav-title">WEB SHIPPING</span>
        </div>
        <div className="nav-right">
          <div className="active-badge" onClick={() => setShowOnlineUsers(true)} style={{ cursor: 'pointer' }}>
            <span className="dot"></span>
            {activeUsers} Online
          </div>
          <button className="nav-btn feedback" onClick={() => setShowFeedbackModal(true)}>Feedback</button>
          <button className="nav-btn logout" onClick={handleLogout}>Logout</button>
        </div>
      </nav>

      <div className="main-content">
        {renderTool()}
      </div>

      {showFeedbackModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Send Feedback</h3>
            <div className="form-group">
              <label>Subject</label>
              <input value={feedbackSubject} onChange={(e) => setFeedbackSubject(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Message</label>
              <textarea value={feedbackMessage} onChange={(e) => setFeedbackMessage(e.target.value)} rows={5} />
            </div>
            <div className="modal-actions">
              <button className="btn secondary" onClick={() => setShowFeedbackModal(false)}>Cancel</button>
              <button className="btn primary" onClick={handleFeedback} disabled={feedbackSending}>
                {feedbackSending ? "Sending..." : "Send Feedback"}
              </button>
            </div>
          </div>
        </div>
      )}



      <Routes>
        <Route path="/shipment-details" element={<ShipmentDetails />} />
      </Routes>
    </div>
  );
};

export default App;
